import { Modal, Typography } from 'antd'
import { NextRouter, Router, useRouter } from 'next/router'
import React, { ReactNode, useCallback, useEffect } from 'react'
import { useIntl } from 'react-intl'

import { buildI18nFunction } from '@/utils/messages'

type useLeavePageConfirmProps = {
  message?: string | ReactNode
  isPaginated?: boolean
}

type state = {
  nextRoute: NextRouter | null
  confirmed: boolean
  isActive: boolean
}

type actionTypes =
  | 'toNextRoute'
  | 'stayHere'
  | 'enableLeavePrevention'
  | 'disableLeavePrevention'
  | 'routeChangeStart'
  | 'pageChange'

function pageConfirmReducer(
  state: state,
  action: {
    type: actionTypes
    payload?: { nextRoute?: NextRouter }
  }
): state {
  let newState: state

  switch (action.type) {
    case 'routeChangeStart':
      newState = {
        ...state,
        nextRoute: action?.payload?.nextRoute,
        confirmed: false,
      }
      break
    case 'toNextRoute':
      newState = {
        ...state,
        confirmed: true,
      }
      break
    case 'stayHere':
      newState = {
        nextRoute: null,
        confirmed: false,
        isActive: true,
      }
      break
    case 'enableLeavePrevention':
      newState = { ...state, isActive: true }
      break
    case 'disableLeavePrevention':
      newState = {
        nextRoute: null,
        confirmed: false,
        isActive: false,
      }
      break
    default:
      throw new Error()
  }
  return newState
}

export function useLeavePageConfirm({
  message,
}: useLeavePageConfirmProps = {}): [
  PreventionComponent: () => JSX.Element,
  showLeavePreventionModal: () => void,
  hideLeavePreventionModal: () => void,
] {
  const [{ nextRoute, confirmed, isActive }, dispatch] = React.useReducer(
    pageConfirmReducer,
    {
      nextRoute: null,
      confirmed: false,
      isActive: false,
    }
  )
  const showLeavePreventionModal = () => {
    dispatch({ type: 'enableLeavePrevention' })
  }
  const hideLeavePreventionModal = () =>
    dispatch({ type: 'disableLeavePrevention' })

  const router = useRouter()
  const handler = useCallback(
    (route: NextRouter) => {
      if (isActive && !confirmed) {
        dispatch({
          type: 'routeChangeStart',
          payload: { nextRoute: route },
        })
        throw 'Route Change Requested'
      }
    },
    [isActive, confirmed]
  )
  const handleWindowClose = (e) => {
    if (isActive) {
      e.preventDefault()
      return (e.returnValue =
        'You have unsaved changes - are you sure you exit Clayton?')
    }
  }

  const cleanUpFn = () => {
    window.removeEventListener('beforeunload', handleWindowClose)
    Router.events.off('routeChangeStart', handler)
  }

  useEffect(() => {
    // when the next route is specified and confirmed reset the state and return a cleanup function
    if (nextRoute && confirmed) {
      dispatch({
        type: 'disableLeavePrevention',
      })
      router.push(nextRoute)
      return cleanUpFn
    }
    // listener for in app route changes
    Router.events.on('routeChangeStart', handler)

    // listener for closing - reloading clayton web app
    window.addEventListener('beforeunload', handleWindowClose)
    return cleanUpFn

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmed, isActive])

  function PreventionComponent(): JSX.Element {
    const t = buildI18nFunction(useIntl().formatMessage)

    return (
      <Modal
        open={!!nextRoute}
        data-cy="LeavePageConfirm"
        onOk={() => dispatch({ type: 'toNextRoute' })}
        onCancel={() => dispatch({ type: 'stayHere' })}
        title="Unsaved changes"
        centered
      >
        <Typography>
          {typeof message === 'undefined'
            ? t(`LeavePageConfirm.default.message`)
            : message}
        </Typography>
      </Modal>
    )
  }
  // tuple return value
  return [
    PreventionComponent,
    showLeavePreventionModal,
    hideLeavePreventionModal,
  ]
}
